// RiesciANonRidere.tsx — v037: /v/<codice>, il video delle risate messo online. Chi lo apre lo guarda con la
// fotocamera accesa: il telefono (come in partita, MediaPipe, niente immagini al server) vede se ride. Se ride ha
// perso; se arriva alla fine ha resistito. Poi: «Sfida {{nome}} dal vivo», «Mandalo a un amico» (il giro continua),
// «Gioca anche tu». Si può guardare anche senza fotocamera, e segnalare il video.
import { useCallback, useEffect, useRef, useState } from "react";
import { useTranslation } from "react-i18next";
import { Avatar } from "../componenti/Avatar.tsx";
import { Faccina } from "../componenti/Faccina.tsx";
import { Finestra } from "../componenti/Finestra.tsx";
import { Indietro } from "../componenti/Indietro.tsx";
import { ConsensoVolto } from "../gioco/ConsensoVolto.tsx";
import { avviaAnalisi, preparaRilevamento, type Analisi } from "../gioco/rilevamento.ts";
import { coriandoli, sbloccaAudio, suono } from "../gioco/festa.ts";
import { linkWhatsapp } from "../gioco/FinePartita.tsx";
import { condividi, vibra } from "../servizi/nativo.ts";
import { voltoAccettato } from "../servizi/account.ts";
import { eMio, fileVideo, leggiVideo, provaFatta, segnalaVideo, togliVideo, type InfoVideo } from "../servizi/video.ts";
import { vai } from "../servizi/percorso.ts";
import { MARCHIO, PERCORSO_PROFILO_PUBBLICO } from "../costanti.ts";
import type { ControlloTema } from "../temi/useTema.ts";

type Passo =
  | { p: "caricamento" }
  | { p: "assente" }
  | { p: "intro" }
  | { p: "volto" }
  | { p: "prova"; conFotocamera: boolean }
  | { p: "esito"; rise: boolean | null };

const MOTIVI = ["offensivo", "non_daccordo", "minore", "altro"] as const;

export function RiesciANonRidere({ codice, tema, onEsci }: { codice: string; tema: ControlloTema; onEsci: () => void }) {
  const { t } = useTranslation();
  const [info, setInfo] = useState<InfoVideo | null>(null);
  const [passo, setPasso] = useState<Passo>({ p: "caricamento" });
  const [conti, setConti] = useState<{ risate: number; resistiti: number } | null>(null);
  const [segnala, setSegnala] = useState(false);
  const [segnalato, setSegnalato] = useState(false);
  const [tolto, setTolto] = useState(false);
  const mio = eMio(codice);
  const tela = useRef<HTMLCanvasElement>(null);
  const resistito = passo.p === "esito" && passo.rise === false;
  useEffect(() => {
    if (resistito && tela.current) return coriandoli(tela.current, ["#ffd60a", "#3de0ff", "#ff5cc8", "#5cf2b0"], 2600);
  }, [resistito]);

  useEffect(() => {
    let vivo = true;
    leggiVideo(codice)
      .then((v) => {
        if (!vivo) return;
        setInfo(v);
        setConti({ risate: v.risate, resistiti: v.resistiti });
        setPasso({ p: "intro" });
      })
      .catch(() => vivo && setPasso({ p: "assente" }));
    return () => {
      vivo = false;
    };
  }, [codice]);

  const inizia = (conFotocamera: boolean) => {
    sbloccaAudio();
    vibra("medio");
    if (conFotocamera && !voltoAccettato()) setPasso({ p: "volto" });
    else setPasso({ p: "prova", conFotocamera });
  };

  const fine = useCallback(
    (rise: boolean | null) => {
      setPasso({ p: "esito", rise });
      if (rise === false) {
        suono("vittoria");
        vibra("successo");
      } else if (rise === true) {
        suono("sconfitta");
        vibra("forte");
      }
      if (rise !== null && !mio)
        void provaFatta(codice, rise)
          .then(setConti)
          .catch(() => undefined);
    },
    [codice, mio],
  );

  const link = typeof window !== "undefined" ? `${window.location.origin}/v/${codice}` : `/v/${codice}`;
  const testoInvio = t("video.manda_testo", { nome: info?.nome ?? "" });
  const manda = async () => {
    vibra("leggero");
    if (await condividi({ titolo: MARCHIO, testo: testoInvio, url: link })) return;
    try {
      await navigator.clipboard.writeText(`${testoInvio} ${link}`);
    } catch {
      // niente appunti
    }
  };

  if (passo.p === "caricamento")
    return (
      <div className="pagina schermata-semplice" aria-busy="true">
        <Faccina tema={tema.tema} />
        <p className="schermata-testo">{t("generale.caricamento")}</p>
      </div>
    );
  if (passo.p === "assente" || !info || tolto)
    return (
      <div className="pagina schermata-semplice">
        <Faccina tema={tema.tema} />
        <p className="schermata-testo" role="alert">
          {tolto ? t("video.tolto") : t("video.assente")}
        </p>
        <button type="button" className="ld-cta ld-cta-centrata" onClick={() => vai("/")}>
          {t("video.gioca_anche_tu")}
        </button>
      </div>
    );
  if (passo.p === "volto") return <ConsensoVolto onAccettato={() => setPasso({ p: "prova", conFotocamera: true })} onEsci={() => setPasso({ p: "intro" })} />;

  const totale = (conti?.risate ?? 0) + (conti?.resistiti ?? 0);
  return (
    <div className="pagina ld-pagina ld-vnr" data-passo={passo.p}>
      <header className="ld-barra">
        <Indietro onClick={onEsci} />
        <span />
      </header>
      {passo.p === "intro" && (
        <main className="ld-vnr-intro">
          <div className="ld-vnr-chi">
            <Avatar quale={info.avatar ?? "mistero"} dimensione={120} anello />
          </div>
          <h1 className="ld-palco-titolo ld-vnr-titolo">{t("video.titolo", { nome: info.nome })}</h1>
          <p className="ld-vnr-sotto">{t("video.sotto")}</p>
          {totale > 0 && <p className="ld-vnr-conti">{t("video.conti", { risate: conti?.risate ?? 0, totale })}</p>}
          <button type="button" className="ld-cta ld-cta-centrata ld-vnr-via" onClick={() => inizia(true)}>
            {t("video.prova")}
          </button>
          <button type="button" className="pulsante pulsante-secondario" onClick={() => inizia(false)}>
            {t("video.solo_guarda")}
          </button>
          <p className="nota-centrata">{t("video.nota_fotocamera")}</p>
          {mio ? (
            <div className="scheda-account ld-vnr-mio">
              <p>{t("video.mio", { visto: info.visto, risate: conti?.risate ?? 0 })}</p>
              <button
                type="button"
                className="pulsante pulsante-pericolo"
                onClick={() =>
                  void togliVideo(codice)
                    .then(() => setTolto(true))
                    .catch(() => undefined)
                }
              >
                {t("video.togli")}
              </button>
            </div>
          ) : (
            <button type="button" className="pulsante-link ld-vnr-segnala" onClick={() => setSegnala(true)}>
              {segnalato ? t("video.segnalato") : t("video.segnala")}
            </button>
          )}
        </main>
      )}
      {passo.p === "prova" && <Prova info={info} conFotocamera={passo.conFotocamera} onFine={fine} />}
      {passo.p === "esito" && (
        <main className="ld-vnr-esito">
          <canvas ref={tela} className="ld-vnr-coriandoli" aria-hidden="true" />
          <h1 className={`ld-palco-titolo ld-vnr-titolo ${passo.rise ? "ld-vnr-rise" : "ld-vnr-resistito"}`}>
            {passo.rise === true ? t("video.hai_riso") : passo.rise === false ? t("video.hai_resistito") : t("video.visto")}
          </h1>
          {conti && totale > 0 && <p className="ld-vnr-conti">{t("video.conti", { risate: conti.risate, totale })}</p>}
          {info.nomeUtente ? (
            <button type="button" className="ld-cta ld-cta-centrata" onClick={() => vai(`${PERCORSO_PROFILO_PUBBLICO}${info.nomeUtente}`)}>
              {t("video.sfida_dal_vivo", { nome: info.nome })}
            </button>
          ) : (
            <button type="button" className="ld-cta ld-cta-centrata" onClick={() => vai("/")}>
              {t("video.gioca_anche_tu")}
            </button>
          )}
          <div className="ld-fine-due">
            <button type="button" className="pulsante ld-fine-whatsapp" onClick={() => window.open(linkWhatsapp(`${testoInvio} ${link}`), "_blank", "noopener")}>
              WhatsApp
            </button>
            <button type="button" className="pulsante pulsante-secondario" onClick={() => void manda()}>
              {t("video.manda")}
            </button>
          </div>
          <button type="button" className="pulsante" onClick={() => setPasso({ p: "prova", conFotocamera: voltoAccettato() })}>
            {t("video.riprova")}
          </button>
          {info.nomeUtente && (
            <button type="button" className="pulsante-link" onClick={() => vai("/")}>
              {t("video.gioca_anche_tu")}
            </button>
          )}
        </main>
      )}
      <Finestra aperta={segnala} titolo={t("video.segnala")} onChiudi={() => setSegnala(false)}>
        <p>{t("video.segnala_testo")}</p>
        <div className="azioni-colonna">
          {MOTIVI.map((m) => (
            <button
              key={m}
              type="button"
              className="pulsante pulsante-secondario"
              onClick={() =>
                void segnalaVideo(codice, m)
                  .then(() => setSegnalato(true))
                  .catch(() => undefined)
                  .finally(() => setSegnala(false))
              }
            >
              {t(`video.motivo_${m}`)}
            </button>
          ))}
        </div>
      </Finestra>
    </div>
  );
}

/** Il video a tutto schermo e, nell'angolo, la propria fotocamera con il «risatometro». */
function Prova({ info, conFotocamera, onFine }: { info: InfoVideo; conFotocamera: boolean; onFine: (rise: boolean | null) => void }) {
  const { t } = useTranslation();
  const video = useRef<HTMLVideoElement>(null);
  const cam = useRef<HTMLVideoElement>(null);
  const analisi = useRef<Analisi | null>(null);
  const finito = useRef(false);
  const [punteggio, setPunteggio] = useState(0);
  const [fotocamera, setFotocamera] = useState<"attesa" | "accesa" | "negata" | "spenta">(conFotocamera ? "attesa" : "spenta");
  const [muto, setMuto] = useState(false);

  const chiudi = useCallback(
    (rise: boolean | null) => {
      if (finito.current) return;
      finito.current = true;
      analisi.current?.ferma();
      video.current?.pause();
      onFine(rise);
    },
    [onFine],
  );

  // la fotocamera (solo la mia faccia, niente audio) e l'analisi sul telefono
  useEffect(() => {
    if (!conFotocamera) return;
    let flusso: MediaStream | null = null;
    let fermo = false;
    const misura = setInterval(() => analisi.current && setPunteggio(analisi.current.punteggi().io), 200);
    void (async () => {
      try {
        flusso = await navigator.mediaDevices.getUserMedia({ video: { facingMode: "user", width: { ideal: 640 } }, audio: false });
        if (fermo) return flusso.getTracks().forEach((x) => x.stop());
        if (cam.current) {
          cam.current.srcObject = flusso;
          await cam.current.play().catch(() => undefined);
        }
        setFotocamera("accesa");
        const fl = await preparaRilevamento();
        if (fermo || !fl || !cam.current || !video.current) return;
        analisi.current = avviaAnalisi(fl, { io: cam.current, avversario: video.current }, () => Date.now(), (chi, e) => {
          if (chi === "io" && e.tipo === "risata") chiudi(true);
        });
      } catch {
        setFotocamera("negata");
      }
    })();
    return () => {
      fermo = true;
      clearInterval(misura);
      analisi.current?.ferma();
      analisi.current = null;
      flusso?.getTracks().forEach((x) => x.stop());
    };
  }, [conFotocamera, chiudi]);

  useEffect(() => {
    const v = video.current;
    if (!v) return;
    // con l'audio se il telefono lo permette (il tocco su «Prova» lo sblocca), altrimenti muto
    v.muted = false;
    void v.play().catch(() => {
      v.muted = true;
      setMuto(true);
      void v.play().catch(() => undefined);
    });
  }, []);

  return (
    <main className="ld-vnr-prova">
      <div className="ld-vnr-schermo">
        <video ref={video} className="ld-vnr-video" src={fileVideo(info.codice)} playsInline preload="auto" onEnded={() => chiudi(fotocamera === "accesa" ? false : null)} />
        {muto && (
          <button
            type="button"
            className="pulsante pulsante-piccolo ld-vnr-audio"
            onClick={() => {
              if (video.current) video.current.muted = false;
              setMuto(false);
            }}
          >
            🔊 {t("video.audio")}
          </button>
        )}
        {conFotocamera && (
          <div className="ld-vnr-io" data-stato={fotocamera}>
            <video ref={cam} muted playsInline autoPlay className="ld-vnr-cam" />
            <span className="ld-vnr-metro" style={{ ["--p" as string]: String(Math.min(1, punteggio)) }} aria-hidden="true" />
          </div>
        )}
      </div>
      <p className="ld-vnr-avviso" role="status" aria-live="polite">
        {fotocamera === "negata" ? t("video.fotocamera_negata") : conFotocamera ? t("gioco.non_ridere") : t("video.solo_guarda_nota")}
      </p>
      <button type="button" className="pulsante" onClick={() => chiudi(conFotocamera && fotocamera === "accesa" ? true : null)}>
        {conFotocamera && fotocamera === "accesa" ? t("video.ho_riso") : t("video.basta")}
      </button>
    </main>
  );
}
